/** Positions, outward normals and UVs: eight floats per vertex. */
export interface MeshGeometry {
  vertices: Float32Array;
  indices: Uint32Array;
}

type Point = readonly [number, number, number];
type UV = readonly [number, number];

/** The top of the artwork points toward -Z when viewed from above. */
const topUV = ([x, , z]: Point): UV => [x + 0.5, z + 0.5];
/** Bottom print reads upright after turning a card over around its long (Z) edge. */
const bottomUV = ([x, , z]: Point): UV => [0.5 - x, z + 0.5];

class MeshBuilder {
  private readonly vertices: number[] = [];
  private readonly indices: number[] = [];

  vertex(position: Point, normal: Point, uv: UV): number {
    const index = this.vertices.length / 8;
    this.vertices.push(...position, ...normal, ...uv);
    return index;
  }

  /** Orient triangles against their vertex normals, so all faces are CCW outside. */
  triangle(a: number, b: number, c: number): void {
    const v = this.vertices;
    const ia = a * 8;
    const ib = b * 8;
    const ic = c * 8;
    const abx = v[ib]! - v[ia]!;
    const aby = v[ib + 1]! - v[ia + 1]!;
    const abz = v[ib + 2]! - v[ia + 2]!;
    const acx = v[ic]! - v[ia]!;
    const acy = v[ic + 1]! - v[ia + 1]!;
    const acz = v[ic + 2]! - v[ia + 2]!;
    const nx = v[ia + 3]! + v[ib + 3]! + v[ic + 3]!;
    const ny = v[ia + 4]! + v[ib + 4]! + v[ic + 4]!;
    const nz = v[ia + 5]! + v[ib + 5]! + v[ic + 5]!;
    const direction = (aby * acz - abz * acy) * nx
      + (abz * acx - abx * acz) * ny
      + (abx * acy - aby * acx) * nz;
    this.indices.push(...(direction >= 0 ? [a, b, c] : [a, c, b]));
  }

  polygon(points: readonly Point[], normal: Point): void {
    const ids = points.map((point) => this.vertex(point, normal, normal[1] < -0.85 ? bottomUV(point) : topUV(point)));
    for (let i = 1; i < ids.length - 1; i += 1) {
      this.triangle(ids[0]!, ids[i]!, ids[i + 1]!);
    }
  }

  finish(): MeshGeometry {
    return {
      vertices: new Float32Array(this.vertices),
      indices: new Uint32Array(this.indices),
    };
  }
}

function unit(x: number, y: number, z: number): Point {
  const length = Math.hypot(x, y, z);
  return [x / length, y / length, z / length];
}

function segmentCount(requested: number): number {
  // Multiples of four include the cardinal points, preserving exact unit bounds.
  return Number.isFinite(requested)
    ? Math.min(128, Math.max(12, Math.round(requested / 4) * 4))
    : 48;
}

/** A closed beveled unit cube, scaled by the renderer into cards, boards or tiles. */
export function createSlab(bevel = 0.04): MeshGeometry {
  const mesh = new MeshBuilder();
  const b = Number.isFinite(bevel) ? Math.min(0.24, Math.max(0, bevel)) : 0.04;
  const inset = 0.5 - b;
  const signs = [-1, 1] as const;

  // Six large, flat faces. Separate vertices deliberately retain crisp bevels.
  for (let axis = 0; axis < 3; axis += 1) {
    const other = [0, 1, 2].filter((value) => value !== axis);
    for (const sign of signs) {
      const normal: [number, number, number] = [0, 0, 0];
      normal[axis] = sign;
      const corners: Point[] = [[-1, -1], [1, -1], [1, 1], [-1, 1]].map(([u, v]) => {
        const point: [number, number, number] = [0, 0, 0];
        point[axis] = sign * 0.5;
        point[other[0]!] = u! * inset;
        point[other[1]!] = v! * inset;
        return point;
      });
      mesh.polygon(corners, normal);
    }
  }

  if (b === 0) return mesh.finish();

  // Twelve edge strips, each joining two neighboring face boundaries.
  for (let free = 0; free < 3; free += 1) {
    const fixed = [0, 1, 2].filter((axis) => axis !== free);
    for (const firstSign of signs) {
      for (const secondSign of signs) {
        const normal: [number, number, number] = [0, 0, 0];
        normal[fixed[0]!] = firstSign * Math.SQRT1_2;
        normal[fixed[1]!] = secondSign * Math.SQRT1_2;
        const corners: Point[] = [[-1, 0], [1, 0], [1, 1], [-1, 1]].map(([end, edge]) => {
          const point: [number, number, number] = [0, 0, 0];
          point[free] = end! * inset;
          point[fixed[0]!] = firstSign * (edge === 0 ? 0.5 : inset);
          point[fixed[1]!] = secondSign * (edge === 0 ? inset : 0.5);
          return point;
        });
        mesh.polygon(corners, normal);
      }
    }
  }

  // Eight corner triangles close the strips without overlapping surfaces.
  for (const x of signs) for (const y of signs) for (const z of signs) {
    mesh.polygon([
      [x * 0.5, y * inset, z * inset],
      [x * inset, y * 0.5, z * inset],
      [x * inset, y * inset, z * 0.5],
    ], unit(x, y, z));
  }
  return mesh.finish();
}

/** A unit cylinder with smooth sides and independent, flat artwork caps. */
export function createDisc(segments = 48): MeshGeometry {
  const mesh = new MeshBuilder();
  const count = segmentCount(segments);
  for (const y of [-0.5, 0.5]) {
    const normal: Point = [0, Math.sign(y), 0];
    const center = mesh.vertex([0, y, 0], normal, [0.5, 0.5]);
    const ring: number[] = [];
    for (let i = 0; i < count; i += 1) {
      const angle = i / count * Math.PI * 2;
      const position: Point = [Math.cos(angle) * 0.5, y, Math.sin(angle) * 0.5];
      ring.push(mesh.vertex(position, normal, y < 0 ? bottomUV(position) : topUV(position)));
    }
    for (let i = 0; i < count; i += 1) mesh.triangle(center, ring[i]!, ring[(i + 1) % count]!);
  }
  const side: number[] = [];
  for (let i = 0; i <= count; i += 1) {
    const angle = i / count * Math.PI * 2;
    const x = Math.cos(angle);
    const z = Math.sin(angle);
    for (const y of [-0.5, 0.5]) {
      side.push(mesh.vertex([x * 0.5, y, z * 0.5], [x, 0, z], [i / count, 0.5 - y]));
    }
  }
  for (let i = 0; i < count; i += 1) {
    mesh.triangle(side[i * 2]!, side[i * 2 + 1]!, side[i * 2 + 2]!);
    mesh.triangle(side[i * 2 + 1]!, side[i * 2 + 3]!, side[i * 2 + 2]!);
  }
  return mesh.finish();
}

/** A turned pawn: weighted foot, tapered stem, collar and rounded head. */
export function createPawn(segments = 48): MeshGeometry {
  const mesh = new MeshBuilder();
  const count = segmentCount(segments);
  // [radius, height]. All solids share local bounds of -0.5 to +0.5.
  const profile: readonly (readonly [number, number])[] = [
    [0.43, -0.5], [0.485, -0.48], [0.5, -0.45], [0.5, -0.41],
    [0.47, -0.37], [0.37, -0.335], [0.315, -0.295], [0.25, -0.22],
    [0.19, -0.09], [0.17, 0.035], [0.205, 0.075], [0.275, 0.1],
    [0.285, 0.13], [0.26, 0.16], [0.175, 0.18], [0.155, 0.21],
    [0.205, 0.245], [0.245, 0.3], [0.255, 0.355], [0.235, 0.415],
    [0.18, 0.465], [0.1, 0.492], [0, 0.5],
  ];

  const rings: number[][] = [];
  for (let row = 0; row < profile.length; row += 1) {
    const [radius, y] = profile[row]!;
    const previous = profile[Math.max(0, row - 1)]!;
    const next = profile[Math.min(profile.length - 1, row + 1)]!;
    const dr = next[0] - previous[0];
    const dy = next[1] - previous[1];
    const ring: number[] = [];
    for (let i = 0; i <= count; i += 1) {
      const angle = i / count * Math.PI * 2;
      const x = Math.cos(angle);
      const z = Math.sin(angle);
      const normal: Point = radius === 0 ? [0, 1, 0] : unit(dy * x, -dr, dy * z);
      ring.push(mesh.vertex([radius * x, y, radius * z], normal, [i / count, 0.5 - y]));
    }
    rings.push(ring);
  }

  for (let row = 0; row < profile.length - 1; row += 1) {
    const lower = rings[row]!;
    const upper = rings[row + 1]!;
    for (let i = 0; i < count; i += 1) {
      mesh.triangle(lower[i]!, upper[i]!, lower[i + 1]!);
      // The final ring is an apex; avoid zero-area triangles there.
      if (profile[row + 1]![0] > 0) mesh.triangle(upper[i]!, upper[i + 1]!, lower[i + 1]!);
    }
  }

  const baseCenter = mesh.vertex([0, -0.5, 0], [0, -1, 0], [0.5, 0.5]);
  const base: number[] = [];
  for (let i = 0; i < count; i += 1) {
    const angle = i / count * Math.PI * 2;
    const position: Point = [Math.cos(angle) * profile[0]![0], -0.5, Math.sin(angle) * profile[0]![0]];
    base.push(mesh.vertex(position, [0, -1, 0], topUV(position)));
  }
  for (let i = 0; i < count; i += 1) mesh.triangle(baseCenter, base[i]!, base[(i + 1) % count]!);
  return mesh.finish();
}

/** A one-sided horizontal unit plane, with its visible face pointing up. */
export function createPlane(): MeshGeometry {
  const mesh = new MeshBuilder();
  mesh.polygon([
    [-0.5, 0, -0.5], [0.5, 0, -0.5], [0.5, 0, 0.5], [-0.5, 0, 0.5],
  ], [0, 1, 0]);
  return mesh.finish();
}

/** Reusable molded train miniature, long axis X: chassis, coach, cab and chimney. */
export function createTrain(): MeshGeometry {
  const parts: readonly [Point, Point][] = [
    [[0, -0.34, 0], [1, 0.2, 0.88]],
    [[-0.06, -0.02, 0], [0.7, 0.42, 0.7]],
    [[0.3, 0.12, 0], [0.3, 0.76, 0.88]],
    [[-0.34, 0.24, 0], [0.12, 0.32, 0.32]],
    [[-0.32, -0.42, 0], [0.21, 0.16, 1]],
    [[0.3, -0.42, 0], [0.21, 0.16, 1]],
  ];
  const vertices: number[] = [], indices: number[] = [];
  for (const [position, size] of parts) {
    const mesh = createSlab(0.09), offset = vertices.length / 8;
    for (let i = 0; i < mesh.vertices.length; i += 8) {
      for (let axis = 0; axis < 3; axis++) vertices.push(mesh.vertices[i + axis]! * size[axis]! + position[axis]!);
      const normal = unit(mesh.vertices[i + 3]! / size[0], mesh.vertices[i + 4]! / size[1], mesh.vertices[i + 5]! / size[2]);
      vertices.push(...normal, mesh.vertices[i + 6]!, mesh.vertices[i + 7]!);
    }
    indices.push(...Array.from(mesh.indices, index => index + offset));
  }
  return { vertices: new Float32Array(vertices), indices: new Uint32Array(indices) };
}
